import React from 'react'
import { format, parse } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import withWidth from '@material-ui/core/withWidth'
import Grid from '@material-ui/core/Grid'

import { useParcelStatistics } from '@graniot/common/adapters/sentinel'
import { useCustomLayer } from '@graniot/common/adapters/customlayers'
import {
	useFetchPlants,
	useSavePlantsMutation,
	useRequestPlantsAnalysisMutation,
} from '@graniot/common/adapters/plants'
import RequestPlants from './RequestPlants'
import ShowPlants from './ShowPlants'
import GraniotSnackbar from '@graniot/common/utils/snackbar'
import CenteredProgress from '../../../../utils/centeredprogress'

const useStyles = makeStyles((theme) => ({
	imgParcel: {
		maxHeight: 400,
		padding: theme.spacing(1),
	},
	imgContainer: {
		minHeight: 400,
	},
	divider: {
		margin: theme.spacing(2),
	},
	pickers: {
		marginTop: theme.spacing(2),
	},
	loading: {
		marginBottom: theme.spacing(20),
		marginTop: theme.spacing(20),
	},
	small: {
		width: theme.spacing(4),
		marginLeft: theme.spacing(0.5),
		[theme.breakpoints.down('sm')]: {
			width: theme.spacing(3),
		},
	},
	fixGridSwitch: {
		marginLeft: theme.spacing(1.5),
	},
}))

const renderImageListReducer = (state, action) => {
	switch (action.type) {
		case 'ADD': {
			return state.concat([action.payload])
		}
		case 'REMOVE': {
			return state.filter((x) => x.date !== action.payload.date)
		}
		case 'RESET': {
			return []
		}
		default:
			return state
	}
}

export function zonesReducer(state, action) {
	if (action === undefined) return

	switch (action.type) {
		case 'rate':
			return {
				...state,
				features: state.features.map((zone) => {
					if (zone.id === action.value.zone)
						return {
							...zone,
							properties: {
								...zone.properties,
								rate: action.value.rate,
							},
						}
					return zone
				}),
			}
		case 'seed':
			return { ...action.value }
		case 'reset':
			return null
		default:
			throw new Error()
	}
}

const TreeMap = ({ layers, parcel, selectedResolution, width, farm }) => {
	const classes = useStyles()
	const { t } = useTranslation()

	const [selectedLayer, setSelectedLayer] = React.useState(layers.find(item => item.resolution != null))
	const [indexFilter, setIndexFilter] = React.useState(null)
	const [sliderChange, setSliderChange] = React.useState(false)
	const [imageUrl, setImageUrl] = React.useState(null)
	const [featureStats, setFeatureStats] = React.useState(null)
	const [maxCC, setMaxCC] = React.useState(['10x10 meters', '3x3 meters'].includes(selectedResolution.resolution) ? 0.9 : 1)
	const [toDate, setToDate] = React.useState(format(new Date(), 'yyyy-LL-dd'))
	const [zones, setZones] = React.useState(3)
	const [zonedGeom, setZonedGeom] = React.useReducer(zonesReducer, null)
	const [startVegetation, setStartVegetation] = React.useState({
		image: null,
		date: null,
	})
	const [imagesDate, dispatchRenderImage] = React.useReducer(
		renderImageListReducer,
		[]
	)
	const [fromDate, setFromDate] = React.useState(
		`01/01/${
			selectedResolution.id === 1
				? 2017
				: selectedResolution.id === 2
				? 2019
				: 2021
		}`
	)

	const [featuresPlantsAnalysis, setFeaturesPlantsAnalysis] =
		React.useState(null)
	const [repeatPlantsAnalysis, setRepeatPlantsAnalysis] =
		React.useState(false)
	// An upload writes the plants straight to the parcel, so the view must
	// leave the empty state on its own. refetchPlants() alone cannot do it:
	// the plants query is disabled until a layer and an analysis date exist,
	// and then the refetch is a no-op and the dropzones stay up.
	const [justUploaded, setJustUploaded] = React.useState(false)
	const [openSnackbar, setOpenSnackbar] = React.useState(null)
	const [statusSnackbar, setStatusSnackbar] = React.useState(null)
	const [messageSnackbar, setMessageSnackbar] = React.useState(null)

	// in this way the loader on all page is shown only the first time
	const [dataPlantsCopy, setDataPlantsCopy] = React.useState(undefined)

	const indexOfDate2 = (date) => {
		return featureStats.findIndex((d) => {
			return new Date(d.date) - parse(date, 'dd/MM/yyyy', new Date()) > 0
		})
	}

	const handleChangeStartVegetation = (comparedDate) => {
		setStartVegetation(featureStats[indexOfDate2(comparedDate)])
	}

	const handleCloudCoverageChange = (event, newValue) => {
		setMaxCC(newValue)
	}

	const getCustomLayerScript = () => {
		if (customLayer) return `&EVALSCRIPTURL=${customLayer.EVALSCRIPTSURL}`
		return ''
	}

	const handleSavePlants = () => {
		if (featuresPlantsAnalysis) {
			mutateSavePlants.mutate(
				[featuresPlantsAnalysis, parcel.id],
				{
					onSuccess: async (data) => {
						setStatusSnackbar('success')
						setMessageSnackbar(t('Plants saved'))
						setRepeatPlantsAnalysis(false)
						setFeaturesPlantsAnalysis(null)
					},
					onError: async (data) => {},
					onSettled: async (data) => {
						setOpenSnackbar(true)
					},
				}
			)
		}
	}

	const mutateRequestPlantsAnalysis = useRequestPlantsAnalysisMutation()
	const mutateSavePlants = useSavePlantsMutation()

	const {
		data: customLayer,
		refetch: refetchCustomLayer,
		isLoading: isLoadingCustomLayer,
	} = useCustomLayer(selectedLayer.name, indexFilter)

	const {
		isLoading: isLoadingFIS,
		data: dataFIS,
		refetch: refetchFIS,
	} = useParcelStatistics (
		parcel.id,
		selectedLayer.key,
		maxCC,
		fromDate,
		toDate
	)

	const {
		isLoading: isLoadingPlants,
		data: dataPlants,
		refetch: refetchPlants,
		isSuccess: isSuccessDataPlants,
	} = useFetchPlants(
		parcel.id,
		selectedLayer.name,
		startVegetation?.date,
		customLayer?.EVALSCRIPTSURL,
		indexFilter
	)

	React.useEffect(() => {
		if (dataPlants) setSliderChange(false)
	}, [isSuccessDataPlants])

	React.useEffect(() => {
		if (mutateSavePlants.isSuccess) {
			handleCancelTreeAnalysis()
			refetchPlants()
		}
	}, [mutateSavePlants.isSuccess])

	React.useEffect(() => {
		if (imageUrl) {
			setIndexFilter(null)
			mutateRequestPlantsAnalysis.mutate(
				[imageUrl, parcel.id, selectedLayer.name],
				{
					onSuccess: async (data) => {
						if (data?.features?.length) {
							setFeaturesPlantsAnalysis(data)
						} else {
							setStatusSnackbar('error')
							setMessageSnackbar(t('no_plants_found'))
							setOpenSnackbar(true)
							handleCancelTreeAnalysis()
						}
					},
					onError: async (data) => {},
					onSettled: async (data) => {},
				}
			)
		}
	}, [imageUrl])

	const handleRequestTreeAnalysis = () => {
		if ([3, 4, 5].includes(selectedResolution.id)) {
			setRepeatPlantsAnalysis(true)
			if (featureStats) setFeatureStats(null)
			setImageUrl(
				`${process.env.REACT_APP_BASE_URL_IMAGES}${
					selectedResolution.instance_id
				}?${parcel.properties.image_url}&LAYERS=${
					selectedLayer.name
				}&TIME=${startVegetation?.date}${getCustomLayerScript()}`
			)
		} else {
			setStatusSnackbar('error')
			setMessageSnackbar(t('Plant_analysis_wrong_resolution'))
			setOpenSnackbar(true)
		}
	}

	const handleFeatureStats = () => {
		if (dataFIS) {
			let feature_stats = Object.assign([], dataFIS)
			feature_stats.reverse()
			setFeatureStats(feature_stats)
		}
	}

	const handleCancelTreeAnalysis = () => {
		setFeaturesPlantsAnalysis(null)
		setRepeatPlantsAnalysis(false)
		setImageUrl(null)
		handleFeatureStats()
	}

	React.useEffect(() => {
		if (fromDate) {
			dispatchRenderImage({ type: 'RESET' })
			refetchFIS()
		}
	}, [selectedLayer, maxCC, parcel])

	React.useEffect(() => {
		handleFeatureStats()
	}, [dataFIS])

	React.useEffect(() => {
		if (featureStats && featureStats.length > 0) {
			setStartVegetation(featureStats[featureStats.length - 1])
		}
	}, [featureStats])

	React.useEffect(() => {
		if (startVegetation?.date) {
			dispatchRenderImage({ type: 'ADD', payload: startVegetation })
		}
	}, [startVegetation])


	const updateLayer = (newLayer) => {
		setSelectedLayer(newLayer)
	}

	React.useEffect(() => {
		if (dataPlants?.features?.length > 0) setDataPlantsCopy(dataPlants)
	}, [dataPlants])
	

	return (
		<Grid container spacing={4}>
			{(((isLoadingFIS || isLoadingPlants) && dataPlantsCopy === undefined) ||
				mutateRequestPlantsAnalysis.isLoading ||
				mutateSavePlants.isLoading ||
				(mutateSavePlants.isSuccess &&
					!dataPlants?.features?.length)) &&
			!sliderChange ? (
				<CenteredProgress
					message={
						mutateSavePlants.isLoading
							? t('Saving data')
							: mutateRequestPlantsAnalysis.isLoading
							? t('Plants_analysis_loading')
							: t('Plants_Loading_first_page')
					}
					sx={{ mt: 3 }}
				/>
			) : ((dataPlantsCopy?.features?.length > 0 || justUploaded) &&
					!repeatPlantsAnalysis) ||
			  sliderChange ? (
				<ShowPlants
					isLoadingFIS={isLoadingFIS}
					isLoadingPlants={isLoadingPlants}
					parcel={parcel}
					layers={layers}
					updateLayer={updateLayer}
					dataPlants={dataPlants}
					featureStats={featureStats}
					startVegetation={startVegetation}
					handleChangeStartVegetation={handleChangeStartVegetation}
					fromDate={fromDate}
					imagesDate={imagesDate}
					selectedResolution={selectedResolution}
					maxCC={maxCC}
					handleCloudCoverageChange={handleCloudCoverageChange}
					refetchPlants={refetchPlants}
					indexFilter={indexFilter}
					setIndexFilter={setIndexFilter}
					getCustomLayerScript={getCustomLayerScript}
					classes={classes}
					width={width}
					handleRequestTreeAnalysis={handleRequestTreeAnalysis}
					zones={zones}
					setZonedGeom={setZonedGeom}
					setSliderChange={setSliderChange}
					setStatusSnackbar={setStatusSnackbar}
					setMessageSnackbar={setMessageSnackbar}
					setOpenSnackbar={setOpenSnackbar}
				/>
			) : (
				<RequestPlants
					onUploaded={() => setJustUploaded(true)}
					farm={farm}
					parcel={parcel}
					layers={layers}
					selectedLayer={selectedLayer}
					selectedResolution={selectedResolution}
					startVegetation={startVegetation}
					maxCC={maxCC}
					fromDate={fromDate}
					imagesDate={imagesDate}
					handleRequestTreeAnalysis={handleRequestTreeAnalysis}
					handleCancelTreeAnalysis={handleCancelTreeAnalysis}
					mutateSavePlants={mutateSavePlants}
					handleSavePlants={handleSavePlants}
					featuresPlantsAnalysis={featuresPlantsAnalysis}
					zones={zones}
					setZonedGeom={setZonedGeom}
					setIndexFilter={setIndexFilter}
					refetchPlants={refetchPlants}
					getCustomLayerScript={getCustomLayerScript}
					indexFilter={indexFilter}
					classes={classes}
					setStatusSnackbar={setStatusSnackbar}
					setMessageSnackbar={setMessageSnackbar}
					setOpenSnackbar={setOpenSnackbar}
				/>
			)}
			<GraniotSnackbar
				open={openSnackbar}
				setOpen={setOpenSnackbar}
				status={statusSnackbar}
				message={messageSnackbar}
			/>
		</Grid>
	)
}

export default withWidth()(TreeMap)
